import type { NonNullRef } from '@lemonade-hq/cdk';
import type { OptionallyConditionalItem } from '@lemonade-hq/chat-wizard-common-definitions';
import type { GenericRecordData, RecordShape } from '@lemonade-hq/maschema-schema';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { NOOP } from '@lemonade-hq/ts-helpers';
import type { UniclientAction, UniclientDialogStep } from '@lemonade-hq/uniclient-chat';
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export interface WizardDialogProps {
  readonly name: string;
  readonly steps: UniclientDialogStep[];
  readonly answersSchema: RecordShape;
  readonly persistedAnswers: GenericRecordData;
  readonly onActionRef: NonNullRef<(action?: UniclientAction) => Promise<unknown>>;
  readonly resyncingActions?: Record<string, OptionallyConditionalItem<UniclientAction>>;
  readonly resyncingDebounce?: Milliseconds;
  readonly onSubmit?: (answers: GenericRecordData) => void;
  readonly actionsOverride?: {
    readonly primary?: () => void;
    readonly secondary?: () => void;
  };
  readonly disclaimer?: string;
  readonly dismissible?: boolean;
  // Only applied for mobile viewport
  readonly fullscreen?: boolean;
  readonly preActionCallback?: () => Promise<boolean>;
  readonly loading?: boolean;
  readonly dynamicHeight?: boolean;
  /**
   * Controls close button visibility on mobile. By default, close button is hidden on mobile except in fullscreen mode.
   * Set to `true` only for internal edge cases where explicit user dismissal is required (e.g., survey dialogs).
   * @default false
   */
  readonly showCloseButtonForMobile?: boolean;
  /**
   * Allows us to control overflow css option for the dialog content component.
   * Sometimes we need to show content that is overlapped dialog content component (e.g., animation for rating component).
   * @default true
   */
  readonly enableScroll?: boolean;
}

export type UniclientDialogContextValue = {
  readonly props: WizardDialogProps;
  readonly setProps: Dispatch<SetStateAction<WizardDialogProps>>;
  readonly isLoading: boolean;
  readonly setIsLoading: Dispatch<SetStateAction<boolean>>;
  readonly setIsPrimaryActionDisabled: Dispatch<SetStateAction<boolean>>;
  readonly isPrimaryActionDisabled: boolean;
  readonly dynamicHeight: boolean;
};

export const UniclientDialogContext = createContext<UniclientDialogContextValue>({
  props: {},
  setProps: NOOP,
  isLoading: false,
  setIsLoading: NOOP,
  setIsPrimaryActionDisabled: NOOP,
  isPrimaryActionDisabled: false,
} as unknown as UniclientDialogContextValue);

export const UniclientDialogProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [props, setProps] = useState<WizardDialogProps>({} as WizardDialogProps);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrimaryActionDisabled, setIsPrimaryActionDisabled] = useState(false);
  const value = useMemo(
    () => ({
      props,
      setProps,
      isLoading,
      setIsLoading,
      setIsPrimaryActionDisabled,
      isPrimaryActionDisabled,
      dynamicHeight: props.dynamicHeight ?? false,
      showCloseButtonForMobile: props.showCloseButtonForMobile ?? false,
      enableScroll: props.enableScroll ?? true,
    }),
    [props, isLoading, isPrimaryActionDisabled],
  );

  return <UniclientDialogContext.Provider value={value}>{children}</UniclientDialogContext.Provider>;
};

export function useUniclientDialogContext(): UniclientDialogContextValue {
  return useContext(UniclientDialogContext);
}

export function useSetDialogLoading(loading: boolean): void {
  const { setIsLoading } = useUniclientDialogContext();

  useEffect(() => {
    setIsLoading(loading);
  }, [loading, setIsLoading]);
}
